import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { useBucket } from '@/api/s3';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { BucketTabs, BUCKET_SETTINGS_TABS } from './bucket-subnav';
import { BucketGeneralTab } from './BucketGeneralTab';
import { BucketUtilizationTab } from './BucketUtilizationTab';
import { BucketTagsTab } from './BucketTagsTab';
import { BucketPolicyTab } from './BucketPolicyTab';
import { BucketLoggingTab } from './BucketLoggingTab';
import { BucketObjectLockTab } from './BucketObjectLockTab';

const isSettingsTab = (v: string): boolean =>
  BUCKET_SETTINGS_TABS.some((t) => t.value === v);

/** Bucket settings — one route-driven tab bar (Objects + settings sections). */
export function BucketDetail({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { bucket = '' } = useParams<{ bucket: string }>();
  const [params] = useSearchParams();
  const { data, isLoading } = useBucket(serviceId, bucket);
  const base = `/services/oss/${serviceId}`;

  // Active settings section carried in `?tab=`; fall back to General.
  const tabParam = params.get('tab') ?? 'general';
  const settingsTab = isSettingsTab(tabParam) ? tabParam : 'general';

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <S3ConsoleBreadcrumbs trail={[{ label: 'Buckets', slug: 'buckets' }, { label: bucket }]} />
        }
        title={bucket}
        description="S3-compatible bucket settings."
        actions={
          <Button
            variant="primary"
            startIcon={<FolderOpen size={15} />}
            onClick={() => navigate(`${base}/buckets/${bucket}/objects`)}
          >
            Browse Objects
          </Button>
        }
      />

      <BucketTabs serviceId={serviceId} bucket={bucket} settingsTab={settingsTab} />

      {isLoading ? (
        <Skeleton variant="rect" height={220} />
      ) : (
        <>
          {settingsTab === 'general' && (
            <BucketGeneralTab serviceId={serviceId} bucket={bucket} data={data} />
          )}
          {settingsTab === 'utilization' && (
            <BucketUtilizationTab serviceId={serviceId} bucket={bucket} />
          )}
          {settingsTab === 'tags' && (
            <BucketTagsTab serviceId={serviceId} bucket={bucket} data={data} />
          )}
          {settingsTab === 'policy' && (
            <BucketPolicyTab serviceId={serviceId} bucket={bucket} data={data} />
          )}
          {settingsTab === 'logging' && (
            <BucketLoggingTab serviceId={serviceId} bucket={bucket} />
          )}
          {settingsTab === 'object-lock' && (
            <BucketObjectLockTab serviceId={serviceId} bucket={bucket} />
          )}
        </>
      )}
    </Container>
  );
}
